import { createContext, useContext, useMemo, useReducer } from "react";
import type { ReactNode } from "react";

// Global logging-flow contract (plan todo 10). The Quick Log launcher, the
// bottom-nav central button, the Chat composer affordance, Today, and chat
// cards all drive logging through this provider. Capture flow SCREENS arrive
// in todos 13-18; this module is the stable API they plug into.

export type LoggingFlowKind =
  | "photo" // todo 13: camera capture -> analyze -> review
  | "upload" // todo 13: library upload -> analyze -> review
  | "text" // todo 14: describe meal in text -> analyze -> review
  | "voice" // todo 15: voice note -> transcribe -> analyze -> review
  | "barcode" // todo 16: barcode scan -> OFF lookup -> review
  | "search" // todo 17: food search (memories/custom foods/OFF) -> review
  | "label" // todo 18: nutrition-label scan -> review
  | "quickAdd" // todo 18: manual quick add (kcal may stay unknown) -> review
  | "exercise" // existing Today WorkoutForm, hosted by the flow overlay
  | "weight" // existing Today weight logging, hosted by the flow overlay
  | "library" // navigates to the library screen (placeholder until todo 19)
  | "review"; // todo 12: unified meal review with prefilled items

// One prefilled review item. Mirrors the PlateItem/review contract (todos
// 11-12): unknown nutrition stays undefined, never zero-filled.
export interface LoggingFlowReviewItem {
  readonly name: string;
  readonly quantity?: number;
  readonly unit?: string;
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly dataSource?: string;
  readonly confidence?: string;
}

// Analysis-provided calorie range (todo 13): the analyze* actions return
// nullable range fields; flows attach the range only when it is complete.
// Every field is optional so a MealReviewPayload (review-utils) stays
// assignable to LoggingFlowPayload for startFlow("review", ...).
export interface LoggingFlowUncertainty {
  readonly hasRange?: boolean;
  readonly lowKcal?: number;
  readonly highKcal?: number;
  readonly reason?: string;
}

// Payload shapes per kind (contractual for the flow implementers):
// - "review": { items, plateMode?, source?, uncertainty?, date?, mealType?,
//   name? } — items are prefilled review rows (e.g. from a chat estimate card
//   or the plate); plateMode marks a plate-originated review (todo 11/12);
//   uncertainty/date/mealType/name are the todo-13 analysis extras the Meal
//   Review screen consumes (todo 12).
// - "photo"/"upload": { imageStorageId?, note?, file? } — imageStorageId/note
//   are set by the capture step before handing off to review (todo 13); file
//   carries a photo chosen via the always-mounted native picker (photo-picker)
//   so PhotoLogFlow skips its pick/auto-open step and uploads immediately.
// - "text"/"voice": { note? } — the described/transcribed meal text
//   (todos 14-15).
// - "barcode"/"search"/"label"/"quickAdd": { note?, source? } reserved;
//   todos 16-18 attach lookup context here. "search" additionally accepts
//   { query } — e.g. the barcode not-found fallback pre-seeds the search
//   screen with the scanned code (todos 16-17).
// - "exercise"/"weight"/"library": no payload.
export interface LoggingFlowPayload {
  readonly items?: readonly LoggingFlowReviewItem[];
  readonly plateMode?: boolean;
  readonly source?: string;
  readonly note?: string;
  readonly imageStorageId?: string;
  readonly uncertainty?: LoggingFlowUncertainty;
  readonly date?: number;
  readonly mealType?: string;
  readonly name?: string;
  readonly query?: string;
  readonly file?: File;
}

export interface ActiveLoggingFlow {
  readonly kind: LoggingFlowKind;
  readonly payload?: LoggingFlowPayload;
}

export interface LoggingFlowState {
  readonly launcherOpen: boolean;
  readonly activeFlow: ActiveLoggingFlow | null;
}

export type LoggingFlowAction =
  | { readonly type: "openLauncher" }
  | { readonly type: "closeLauncher" }
  | { readonly type: "startFlow"; readonly kind: LoggingFlowKind; readonly payload?: LoggingFlowPayload }
  | { readonly type: "closeFlow" };

export const INITIAL_LOGGING_FLOW_STATE: LoggingFlowState = {
  launcherOpen: false,
  activeFlow: null,
};

export function loggingFlowReducer(
  state: LoggingFlowState,
  action: LoggingFlowAction,
): LoggingFlowState {
  switch (action.type) {
    case "openLauncher":
      return { ...state, launcherOpen: true };
    case "closeLauncher":
      return { ...state, launcherOpen: false };
    case "startFlow":
      // Starting a flow always closes the launcher; launcher items call
      // startFlow + closeLauncher per the todo-10 contract.
      return {
        launcherOpen: false,
        activeFlow: action.payload === undefined
          ? { kind: action.kind }
          : { kind: action.kind, payload: action.payload },
      };
    case "closeFlow":
      return { ...state, activeFlow: null };
  }
}

export interface LoggingFlowContextValue {
  readonly launcherOpen: boolean;
  readonly activeFlow: ActiveLoggingFlow | null;
  readonly openLauncher: () => void;
  readonly closeLauncher: () => void;
  readonly startFlow: (kind: LoggingFlowKind, payload?: LoggingFlowPayload) => void;
  readonly closeFlow: () => void;
}

const LoggingFlowContext = createContext<LoggingFlowContextValue | null>(null);

export interface LoggingFlowProviderProps {
  readonly children: ReactNode;
  // Test hook: lets renderToStaticMarkup suites render the launcher/overlay
  // in a specific state without interaction.
  readonly initialState?: LoggingFlowState;
}

export function LoggingFlowProvider({ children, initialState }: LoggingFlowProviderProps) {
  const [state, dispatch] = useReducer(
    loggingFlowReducer,
    initialState ?? INITIAL_LOGGING_FLOW_STATE,
  );

  const value = useMemo<LoggingFlowContextValue>(
    () => ({
      launcherOpen: state.launcherOpen,
      activeFlow: state.activeFlow,
      openLauncher: () => {
        console.debug("[Frontend][LoggingFlow] Launcher opened");
        dispatch({ type: "openLauncher" });
      },
      closeLauncher: () => {
        console.debug("[Frontend][LoggingFlow] Launcher closed");
        dispatch({ type: "closeLauncher" });
      },
      startFlow: (kind, payload) => {
        console.log("[Frontend][LoggingFlow] Flow started", {
          kind,
          hasPayload: payload !== undefined,
          prefilledItemCount: payload?.items?.length ?? 0,
        });
        dispatch({ type: "startFlow", kind, payload });
      },
      closeFlow: () => {
        console.debug("[Frontend][LoggingFlow] Flow closed");
        dispatch({ type: "closeFlow" });
      },
    }),
    [state.launcherOpen, state.activeFlow],
  );

  return <LoggingFlowContext.Provider value={value}>{children}</LoggingFlowContext.Provider>;
}

export function useLoggingFlow(): LoggingFlowContextValue {
  const context = useContext(LoggingFlowContext);
  if (context === null) {
    throw new Error(
      "useLoggingFlow must be used within a LoggingFlowProvider (mounted in App around MainApp)",
    );
  }
  return context;
}
